class WallesqueProductZoom extends HTMLElement {
  connectedCallback() {
    this.gallery = this.closest('.product-information__media')?.querySelector('media-gallery');
    this.range = this.querySelector('[data-zoom-range]');
    this.value = this.querySelector('[data-zoom-value]');
    this.zoomOut = this.querySelector('[data-zoom-out]');
    this.zoomIn = this.querySelector('[data-zoom-in]');
    if (!this.gallery || !this.range) return;

    this.scale = 1;
    this.pan = { x: 0, y: 0 };
    this.activeSlide = this.getActiveSlide();
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.range.addEventListener('input', () => this.setScale(Number(this.range.value) / 100), { signal });
    this.zoomOut.addEventListener('click', () => this.setScale(this.scale - 0.25), { signal });
    this.zoomIn.addEventListener('click', () => this.setScale(this.scale + 0.25), { signal });
    this.gallery.addEventListener('wheel', (event) => {
      if (!event.target.closest('slideshow-slide .product-media__image')) return;
      event.preventDefault();
      this.setScale(this.scale + (event.deltaY < 0 ? 0.15 : -0.15));
    }, { passive: false, signal });
    this.gallery.addEventListener('pointerdown', (event) => this.startDrag(event), { signal });
    this.gallery.addEventListener('pointermove', (event) => this.moveDrag(event), { signal });
    this.gallery.addEventListener('pointerup', () => this.endDrag(), { signal });
    this.gallery.addEventListener('pointercancel', () => this.endDrag(), { signal });
    this.gallery.addEventListener('dragstart', (event) => {
      if (this.scale > 1 && event.target.closest('.product-media__image')) event.preventDefault();
    }, { signal });

    this.observer = new MutationObserver(() => {
      const slide = this.getActiveSlide();
      if (slide !== this.activeSlide) {
        this.reset();
        this.activeSlide = slide;
      }
    });
    this.observer.observe(this.gallery, { attributes: true, attributeFilter: ['aria-hidden'], subtree: true });
    this.update();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.observer?.disconnect();
  }

  getActiveSlide() {
    return this.gallery.querySelector('slideshow-slide[aria-hidden="false"]') || this.gallery.querySelector('slideshow-slide');
  }

  setScale(scale) {
    this.scale = Math.max(1, Math.min(3, Math.round(scale * 20) / 20));
    if (this.scale === 1) this.pan = { x: 0, y: 0 };
    this.clampPan();
    this.update();
  }

  clampPan() {
    const rect = this.getActiveSlide()?.getBoundingClientRect();
    if (!rect) return;
    const maxX = rect.width * (this.scale - 1) / 2;
    const maxY = rect.height * (this.scale - 1) / 2;
    this.pan.x = Math.max(-maxX, Math.min(maxX, this.pan.x));
    this.pan.y = Math.max(-maxY, Math.min(maxY, this.pan.y));
  }

  update() {
    const slide = this.getActiveSlide();
    const image = slide?.querySelector('.product-media__image');
    if (image) image.style.transform = `translate(${this.pan.x}px, ${this.pan.y}px) scale(${this.scale})`;
    slide?.classList.toggle('is-zoomed', this.scale > 1);
    this.range.value = String(Math.round(this.scale * 100));
    this.value.textContent = `${Math.round(this.scale * 100)}%`;
    this.zoomOut.disabled = this.scale === 1;
    this.zoomIn.disabled = this.scale === 3;
  }

  reset() {
    this.gallery.querySelectorAll('slideshow-slide').forEach((slide) => {
      slide.classList.remove('is-zoomed', 'is-dragging');
      const image = slide.querySelector('.product-media__image');
      if (image) image.style.transform = '';
    });
    this.scale = 1;
    this.pan = { x: 0, y: 0 };
    this.update();
  }

  startDrag(event) {
    if (this.scale === 1 || event.target !== this.getActiveSlide()?.querySelector('.product-media__image')) return;
    event.preventDefault();
    this.drag = { x: event.clientX, y: event.clientY, panX: this.pan.x, panY: this.pan.y };
    this.getActiveSlide().classList.add('is-dragging');
    event.target.setPointerCapture(event.pointerId);
  }

  moveDrag(event) {
    if (!this.drag) return;
    this.pan.x = this.drag.panX + event.clientX - this.drag.x;
    this.pan.y = this.drag.panY + event.clientY - this.drag.y;
    this.clampPan();
    this.update();
  }

  endDrag() {
    this.drag = null;
    this.getActiveSlide()?.classList.remove('is-dragging');
  }
}

if (!customElements.get('wallesque-product-zoom')) {
  customElements.define('wallesque-product-zoom', WallesqueProductZoom);
}
